import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	SiteFooter,
	SiteHeader,
	type FooterData,
	type HeaderData,
	type LanguageOption,
	RendererRuntimeProvider
} from "@apa/core-renderers";
import { PREVIEW_LOGO, PREVIEW_FOOTER_LOGO, type DisplayViewportKey } from "./previewUtils";
import { createRuntimeImageComponent } from "../../Helper/storage";

interface LayoutPreviewSurfaceProps {
	headerData: HeaderData | null;
	footerData: FooterData | null;
	language: string;
	languageOptions: LanguageOption[];
	defaultLanguageCode: string;
	viewport: DisplayViewportKey;
	width: number | null;
}

/** Full layout preview surface with header, placeholder content and footer. */
const LayoutPreviewSurface: React.FC<LayoutPreviewSurfaceProps> = ({
	headerData,
	footerData,
	language,
	languageOptions,
	defaultLanguageCode,
	viewport,
	width
}) => {
	const previewReady = Boolean(headerData && footerData);
	const [lastLinkHref, setLastLinkHref] = useState<string | null>(null);

	useEffect(() => {
		if (!lastLinkHref) return;
		const timer = window.setTimeout(() => setLastLinkHref(null), 2200);
		return () => window.clearTimeout(timer);
	}, [lastLinkHref]);

	const handleLinkClick = useCallback(({ href }: { href?: string }) => {
		setLastLinkHref(href ? href.toString() : "Lien sans href");
	}, []);

	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			onLinkClick: handleLinkClick,
			imageComponent: runtimeImageComponent
		}),
		[handleLinkClick, runtimeImageComponent]
	);
	const frameStyle = useMemo(
		() => (width ? { width: `${Math.round(width)}px`, maxWidth: "100%" } : { width: "100%", maxWidth: "100%" }),
		[width]
	);

	if (!previewReady) {
		return (
			<div className="layout-preview-surface">
				<div className="layout-preview-empty">
					<p className="text-base font-semibold text-slate-700">Layout preview not available</p>
					<p className="mt-1 text-sm text-slate-500">
						Provide at least one navigation group and one footer block, then open the preview again.
					</p>
				</div>
			</div>
		);
	}

	const header = headerData!;
	const footer = footerData!;

	return (
		<div className="layout-preview-surface">
			<div className="layout-preview-frame" data-viewport={viewport} style={frameStyle}>
				<RendererRuntimeProvider value={runtimeConfig}>
					<div className="layout-preview-canvas">
						<div className="layout-preview-header">
							<SiteHeader
								data={header}
								logoSrc={PREVIEW_LOGO}
								logoAlt={header.logoAlt}
								homeHref="/"
								language={language}
								languageOptions={languageOptions}
								defaultLanguageCode={defaultLanguageCode}
							/>
						</div>
						<div className="layout-preview-main">
							<p className="layout-preview-pill">Hero section</p>
							<div className="layout-preview-hero">
								<h2>Représenter la stabilité et la croissance</h2>
								<p>
									Use this preview to verify menu groupings, quick links, footer addresses and social channels. Content shown here is
									placeholder text.
								</p>
							</div>
							<div className="layout-preview-grid">
								{[1, 2, 3].map((index) => (
									<div key={`preview-card-${index}`} className="layout-preview-card">
										<p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Section</p>
										<h3>Section titre #{index}</h3>
										<p>
											Preview card to visualize spacing between header and footer. Replace with your own page modules on the public site.
										</p>
									</div>
								))}
							</div>
						</div>
						<div className="layout-preview-footer">
							<SiteFooter data={footer} logoSrc={PREVIEW_FOOTER_LOGO} logoAlt={header.logoAlt} />
						</div>
						{lastLinkHref && (
							<div className="layout-preview-toast" role="status">
								Navigation désactivée (aperçu) : {lastLinkHref}
							</div>
						)}
					</div>
				</RendererRuntimeProvider>
			</div>
		</div>
	);
};

export default LayoutPreviewSurface;


